<script setup lang="ts">
import { DiscountGridElementProps } from "./DiscountGridElement.props"
import { getFormatDate } from "~/utils/date"
import { NotificationAlertProps } from "../NotificationAlert.props"
import LabelWithIconAndLink from "~/components/Utils/LabelWithIconAndLink.vue"

const props = defineProps<DiscountGridElementProps>()

const { t } = useI18n()

const { applyCoupon } = useCart()
const { addNotification } = useNotification()
const { copy } = useClipboard()
const { cart } = useCart()

const isPromoCodeCopied = ref(false)

const convertedDate = getFormatDate(props.expirationDate)
const currentDate = Date.now()
const isPromoValid = props.expirationDate - currentDate > 0

const applyToCart = () => {
  const notificationWarn: NotificationAlertProps = {
    id: Math.random(),
    type: "warning",
    notificationAlertText: t("discounts.codeAppliedError"),
    isWithCloseIcon: true
  }
  applyCoupon(props.promoCode)
    .then(() => {
      isPromoCodeCopied.value = true
    })
    .catch(() => addNotification(notificationWarn))
}

const handleCopyToClipboard = () => {
  copy(props.promoCode)
  const notification: NotificationAlertProps = {
    id: Math.random(),
    type: "success",
    notificationAlertText: t("discounts.copyCodeToClipboard"),
    isWithCloseIcon: true
  }

  addNotification(notification)
}

//Watch for a change in the coupon code and re enable the button when the code
//is different from the one applied previously
watch(
  () => cart.value?.coupon_code,
  (newCoupon) => {
    if (newCoupon !== props.promoCode) {
      isPromoCodeCopied.value = false
    }
  }
)
</script>

<template>
  <div
    class="
      bg-white
      flex
      h-full
      w-full
      transform-gpu
      flex-col
      gap-4
      rounded-lg
      p-4
      drop-shadow-01
      transition
      hover:drop-shadow-02
    "
  >
    <div class="info flex flex-1 items-start gap-4">
      <UtilsWrapperImage
        v-if="image.image"
        :image="image.image"
        class="aspect-square h-[136px] w-[136px] overflow-hidden rounded-lg"
        width="136"
        height="136"
      />
      <div class="text-info flex flex-1 flex-col gap-2">
        <LabelWithIconAndLink
          v-if="label?.label"
          :label="label?.label"
          :labelIcon="label?.labelIcon"
          :labelLink="label?.labelLink"
        />
        <h4 class="dolphin-bold md:hyppo-bold text-green-main">
          {{ title }}
        </h4>
        <div if="expirationDate" class="flex items-start gap-2">
          <strong>{{ $t("discounts.validity") }}:</strong>
          <p v-if="isPromoValid">
            {{ $t("discounts.until") }} <strong>{{ convertedDate }}</strong> -
            <span>{{ $t("discounts.nonCumulative") }}</span>
          </p>
          <UtilsMarkdown
            v-else
            :content="$t('discounts.expiredOn', { date: convertedDate })"
          />
        </div>
      </div>
    </div>

    <div class="grid grid-cols-1 gap-2 md:grid-cols-2">
      <div class="relative w-full">
        <div class="relative overflow-hidden rounded-lg">
          <UtilsButton
            :text="`${promoCode}`"
            fluid
            theme="outlined-green"
            :class="['custom-btn-discount-code-brand unlocked']"
            order="last"
            @click="handleCopyToClipboard"
          >
            <template #icon
              ><UtilsIcon
                decoding="async"
                fetch-priority="low"
                name="Copy.svg"
                color="green-main"
                class="h-5 w-5"
            /></template>
          </UtilsButton>
        </div>
      </div>

      <UtilsButton
        :class="{
          '[&>span]:!bg-positive-main [&>span]:!text-white': isPromoCodeCopied
        }"
        :text="
          isPromoCodeCopied
            ? $t(`discounts.applied.ctaText`)
            : $t(`discounts.ready.ctaText`)
        "
        fluid
        order="last"
        :is-disabled="isPromoCodeCopied"
        @click="applyToCart"
      >
        <template #icon>
          <IconsCheckmark v-if="isPromoCodeCopied" class="h-5 w-5" />
        </template>
      </UtilsButton>
    </div>
  </div>
</template>
